import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { pickLocale, type Characteristic } from '../../schema';
import type {
    CharacteristicRenderer,
    RendererCharacteristic,
    RendererContext,
    RendererValue,
} from '../../api/renderers';
import {
    requireSelectionType,
    requireValueId,
    selectionValue,
    type CharacteristicRef,
    type ValueRef,
} from '../../api/instance';
import { publicApi } from '../../public-api';
import { useRuleResults } from '../../rules/RuleResultsContext';
import {
    useComponent,
    useEffectiveValuePrice,
    useHostPriceConverter,
    useValuePriceFormatter,
} from '../../state/ComponentContext';
import { useConfigentoStoreRef, useValidationErrorsFor } from '../../state/StoreContext';
import { useEffectiveSelections } from '../../state/useEffectiveSelections';
import { useInstanceId } from '../../state/InstanceContext';

interface CustomRendererHostProps {
    name: string;
    renderer: CharacteristicRenderer;
    /** The Characteristics this widget serves; the first is its own. */
    members: readonly Characteristic[];
    locale: string | undefined;
    /** Built-in view, shown when the renderer throws. */
    fallback: ReactNode;
}

/**
 * Hands a container to a host renderer and keeps it informed. React renders
 * the container once and never touches its content; the renderer owns it.
 * A renderer that throws is torn down and the built-in view takes over, so
 * one broken widget never blanks the configurator.
 */
export function CustomRendererHost({ name, renderer, members, locale, fallback }: CustomRendererHostProps) {
    const [failed, setFailed] = useState<CharacteristicRenderer | null>(null);
    if (failed === renderer) return <>{fallback}</>;
    return (
        <RendererMount
            name={name}
            renderer={renderer}
            members={members}
            locale={locale}
            onFail={(err) => {
                console.error(`[Configento] renderer "${name}" failed — showing the built-in view.`, err);
                setFailed(renderer);
            }}
        />
    );
}

function RendererMount({
    name,
    renderer,
    members,
    locale,
    onFail,
}: {
    name: string;
    renderer: CharacteristicRenderer;
    members: readonly Characteristic[];
    locale: string | undefined;
    onFail: (err: unknown) => void;
}) {
    const elRef = useRef<HTMLDivElement | null>(null);
    const data = useRendererData(members, locale);
    const signature = useMemo(() => JSON.stringify(data), [data]);
    const store = useConfigentoStoreRef();
    const instanceId = useInstanceId();

    // The latest data for effects that run on other triggers.
    const latest = useRef({ data, signature, members, onFail });
    latest.current = { data, signature, members, onFail };
    const mounted = useRef<{ alive: boolean; signature: string } | null>(null);

    const buildContext = (token: { alive: boolean }): RendererContext => {
        const { data: d, members: ms } = latest.current;
        const own = d.characteristics[0];
        if (!own) throw new Error(`Configento: renderer "${name}" has no Characteristic.`);
        const target = (ref: CharacteristicRef | undefined): Characteristic => {
            const first = ms[0];
            if (ref === undefined && first) return first;
            const found = ms.find((c) => (typeof ref === 'number' ? c.id === ref : c.altTitle === ref));
            if (!found) {
                throw new Error(`Configento: Characteristic ${String(ref)} is not part of renderer "${name}".`);
            }
            return found;
        };
        const write = (fn: () => void): void => {
            // Calls after destroy() are ignored: the widget is gone.
            if (token.alive) fn();
        };
        return {
            characteristic: own,
            characteristics: d.characteristics,
            locale: d.locale,
            instance: instanceId ? publicApi.instances.get(instanceId) ?? null : null,
            selectValue: (value: ValueRef | null, ref?: CharacteristicRef) => write(() => {
                const c = target(ref);
                requireSelectionType(c, 'single');
                store.getState().selectValue(c.id, value === null ? null : requireValueId(c, value));
            }),
            toggleValue: (value: ValueRef, ref?: CharacteristicRef) => write(() => {
                const c = target(ref);
                requireSelectionType(c, 'multi');
                store.getState().toggleValue(c.id, requireValueId(c, value));
            }),
            setText: (text: string, ref?: CharacteristicRef) => write(() => {
                const c = target(ref);
                requireSelectionType(c, 'text');
                store.getState().setText(c.id, String(text));
            }),
            setQuantity: (value: ValueRef, quantity: number, ref?: CharacteristicRef) => write(() => {
                const c = target(ref);
                requireSelectionType(c, 'quantities');
                store.getState().setQuantity(c.id, requireValueId(c, value), quantity);
            }),
            reset: (ref?: CharacteristicRef) => write(() => {
                store.getState().resetSelection(target(ref).id);
            }),
        };
    };

    // Mount once per renderer and set of served Characteristics; a
    // re-registered renderer, or a combined widget gaining or losing a
    // member, starts over.
    const memberKey = members.map((c) => c.id).join(' ');
    useEffect(() => {
        const el = elRef.current;
        if (!el) return;
        const token = { alive: true, signature: latest.current.signature };
        try {
            renderer.mount(el, buildContext(token));
            mounted.current = token;
        } catch (err) {
            token.alive = false;
            latest.current.onFail(err);
            return;
        }
        return () => {
            token.alive = false;
            mounted.current = null;
            try {
                renderer.destroy?.(el);
            } catch (err) {
                console.error(`[Configento] renderer "${name}" threw in destroy():`, err);
            }
            el.replaceChildren();
        };
        // buildContext reads everything else through `latest`.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [renderer, name, memberKey]);

    // Update only when something the renderer sees has changed.
    useEffect(() => {
        const token = mounted.current;
        const el = elRef.current;
        if (!token || !el || token.signature === signature) return;
        token.signature = signature;
        if (!renderer.update) return;
        try {
            renderer.update(buildContext(token), el);
        } catch (err) {
            latest.current.onFail(err);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [signature, renderer]);

    return <div ref={elRef} className="cfg-custom" data-configento-renderer={name} />;
}

interface RendererData {
    characteristics: RendererCharacteristic[];
    locale: string;
}

function useRendererData(members: readonly Characteristic[], locale: string | undefined): RendererData {
    const component = useComponent();
    const rules = useRuleResults();
    const selections = useEffectiveSelections();
    const ids = useMemo(() => members.map((c) => c.id), [members]);
    const errors = useValidationErrorsFor(ids);
    const effectivePrice = useEffectiveValuePrice();
    const formatPrice = useValuePriceFormatter();
    const toHost = useHostPriceConverter();
    const loc = locale ?? component.defaultLocale ?? 'de_DE';

    return useMemo(() => {
        const characteristics = members.map((c): RendererCharacteristic => {
            const sel = selections[c.id];
            const pickedId = sel?.kind === 'single' ? sel.valueId : null;
            const pickedIds = sel?.kind === 'multi' ? sel.valueIds : [];
            const values: RendererValue[] = [];
            if ('values' in c && Array.isArray(c.values)) {
                const picked = pickedId === null ? undefined : c.values.find((v) => v.id === pickedId);
                const base = picked ? effectivePrice(picked, c) : undefined;
                for (const v of c.values) {
                    if (rules.blacklistedValues.has(v.id)) continue;
                    const amount = effectivePrice(v, c);
                    values.push({
                        id: v.id,
                        key: v.value ?? String(v.id),
                        label: pickLocale(v.title, loc) ?? '',
                        info: pickLocale(v.info, loc) ?? null,
                        image: v.thumbnail ?? v.image ?? null,
                        price: toHost(amount),
                        priceLabel: formatPrice(amount, base),
                        selected: v.id === pickedId || pickedIds.includes(v.id),
                    });
                }
            }
            const entry: RendererCharacteristic = {
                id: c.id,
                key: c.altTitle ?? String(c.id),
                type: c.type,
                title: pickLocale(c.title, loc) ?? '',
                info: pickLocale(c.info, loc) ?? null,
                required: 'isRequired' in c && c.isRequired === true,
                values,
                selection: selectionValue(c, sel),
                errors: errors.filter((e) => e.characteristicId === c.id),
            };
            if (c.type === 'text' || c.type === 'area') {
                const min = Number(c.minStringLength);
                const max = Number(c.maxStringLength ?? c.maxCharacters);
                entry.textConstraints = {
                    ...(c.minStringLength && Number.isFinite(min) ? { minLength: min } : {}),
                    ...((c.maxStringLength ?? c.maxCharacters) && Number.isFinite(max) ? { maxLength: max } : {}),
                    ...(c.type === 'text' && c.textValidate ? { validator: c.textValidate } : {}),
                };
            }
            return entry;
        });
        return { characteristics, locale: loc };
    }, [members, selections, rules, errors, effectivePrice, formatPrice, toHost, loc]);
}
